/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-10-16 11:21:53
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import isEmpty from "lodash-es/isEmpty";
import isEqual from "lodash-es/isEqual";
import has from "lodash-es/has";
import filter from "lodash-es/filter";
import { produce } from "immer";

import { Configure } from "@/frameworks/libraries/utilities";
import { Properties } from "@/frameworks/libraries/properties";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";
import { PeopleService, SpaceService, UserService } from "@/frameworks/core/API";
import { GroupActions } from "@/frameworks/core/persistence";
import { checkPriorityCurrentSpaceType } from "@/frameworks/core/utils";

import type { UserCurrentSpace, UserDispatch } from "./states";
import { APIErrorCode } from "@/frameworks/core/commons";
import { _T } from "@rfice/language";

const LOGTAG = "#User:Actions#";

export default class UserActions implements UserDispatch {
	private static _instance: UserActions | null = null;
	public static get instance() {
		if (!UserActions._instance) {
			UserActions._instance = new UserActions();
		}
		return UserActions._instance;
	}

	private constructor() {}
	private userAPI = new UserService({});

	// for test
	set_blocked_users(blocked_users: string[]) {}
	add_blocked_user(userID: string) {}
	remove_blocked_user(userID: string) {}

	/**
	 * Handles an incoming message received via a STOMP protocol.
	 *
	 * @param {Object} message - The message frame containing the type and payload.
	 * @param {string} message.type - The type of the message (e.g., "userUpdated", "userBlock").
	 * @param {Object} message.payload - The payload of the message, varies based on message type.
	 * @param {string} topic - The topic from which the message was received.
	 * @return {Promise<void>} A promise resolving to void once message processing is complete.
	 */
	public async on_message_received(
		{ type, payload }: Message.Frame<Message.StompMessageTypes, any, Message.Options>,
		topic: string
	): Promise<void> {
		if (!["userUpdated", "userBlock"].includes(type)) {
			return;
		}

		switch (type) {
			case "userUpdated": {
				const userUpdated = payload as PushServices.User.UserUpdated.Data;
				// console.debug(LOGTAG, "on_message_received userUpdated", userUpdated);
				await this.update_user(userUpdated);
				break;
			}

			case "userBlock": {
				const { userProfile: userInfo, blocked } = payload as PushServices.User.UserBlock.Data;
				const user = this.get_user(userInfo.id);
				if (user) {
					this.set_user(
						userInfo.id,
						produce(user, (draft) => {
							draft.blockedByMe = blocked;
						})
					);
				}
				break;
			}
		}
	}

	/**
	 * Initializes users by fetching data from an external API and updating
	 * the persistent store with user information.
	 *
	 * @return {Promise<void>} A promise that resolves when the initialization process is complete.
	 */
	public async initialize(): Promise<void> {
		if (Configure.is_authenticated) {
			const { users } = usePersistStore.getState();
			const me = filter(users, "me")[0];

			if (Object.keys(users).length <= 1 || this.is_expired(Configure.userID) || !me) {
				console.log(
					"#UserActions#",
					"initialize use api",
					"me",
					me,
					"is_expired",
					this.is_expired(Configure.userID),
					"users",
					users
				);
				const peopleAPI = new PeopleService({});
				const { data } = await peopleAPI.people.list.execute().catch((error) => {
					console.warn(LOGTAG, "Failed to initialize Groups", error);
					return { data: null };
				});
				if (data) {
					GroupActions.instance.set_groups(data);

					for await (const group of data) {
						for await (const user of group.users) {
							// console.debug(LOGTAG, "Initializing User", user, this.is_expired(user.userId));
							if (!users[user.userId] || this.is_expired(user.userId)) {
								this.set_user(user.userId, user);
							}
						}
					}
				}
			} else {
				console.log("#UserActions#", "initialze use Cache");
			}

			Properties.property = { user: filter(users, "me")[0] };
		}
	}

	private refresh_userID_list = new Set<string>();

	/**
	 * Refreshes the user information for the specified user ID. This method retrieves
	 * the user info from the web service and updates the local store.
	 *
	 * @param {string} userID - The unique identifier of the user whose information needs to be refreshed.
	 * @param {boolean} [clearAll=false] - A flag indicating whether to clear all the existing user data before refreshing.
	 * @return {Promise<Partial<WebServices.User.UserInfo> | null>} Returns a promise that resolves to the partial user information if successful, or null if the operation fails or the user ID is already being refreshed.
	 */
	public async refresh_user(userID: string, clearAll = false): Promise<Partial<WebServices.User.UserInfo> | null> {
		if (userID && Configure.is_authenticated && !this.refresh_userID_list.has(userID)) {
			this.refresh_userID_list.add(userID);
			const { data } = await this.get_user_info(userID);
			if (data) {
				if (!isEmpty(data)) {
					this.set_user(userID, data, clearAll);
				}
				this.refresh_userID_list.delete(userID);
				return this.get_user(userID);
			}
		}
		return null;
	}

	/**
	 * Retrieves the current user's information from the persistent store.
	 *
	 * @return {Partial<WebServices.User.UserInfo>} The current user's info if available, otherwise an object with undefined `activeStatus`.
	 */
	public get me(): Partial<WebServices.User.UserInfo> {
		try {
			const { users } = usePersistStore.getState();
			return filter(users, "me")[0] as WebServices.User.UserInfo;
		} catch {
			return { activeStatus: undefined } as WebServices.User.UserInfo;
		}
	}

	/**
	 * Updates the current user's information and retrieves the latest data.
	 *
	 * @param {WebServices.User.Me.Update.Request | null} info - The new user information to be updated. If null, no update will be performed.
	 * @return {Promise<void>} A Promise that resolves when the update and retrieval process is complete.
	 */
	public async update_me(info: WebServices.User.Me.Update.Request | null): Promise<void> {
		if (info) {
			const { data } = await this.userAPI.user.me.update.execute(info);
			if (data && Object.keys(data).length === 1 && data["profileImagePath"]) {
				// console.debug(LOGTAG, "update_me", info, data, this.me.profileImagePath);
				this.set_user(Configure.userID, { ...data, me: true });
				return;
			}
		}
		const { data } = await this.userAPI.user.me.inquiry.execute();
		data && this.set_user(Configure.userID, { ...data, me: true });
	}

	/**
	 * Retrieves the information of a user by their user ID.
	 *
	 * @param {string} userID - The unique identifier of the user.
	 * @return {Partial<WebServices.User.UserInfo> | null} The user information if found, otherwise null.
	 */
	public get_user(userID: string): Partial<WebServices.User.UserInfo> | null {
		const { users } = usePersistStore.getState();
		if (!users[userID] || this.is_expired(userID)) {
			this.refresh_user(userID).then((user) => {});
		}
		return users[userID] ?? null;
	}

	/**
	 * Updates or sets user information in the persistent store.
	 *
	 * @param {string} userID - The unique identifier of the user.
	 * @param {Partial<WebServices.User.UserInfo>} info - An object containing partial user information to be updated or set.
	 * @return {void} Returns nothing.
	 */
	public set_user(userID: string, info: Partial<WebServices.User.UserInfo>, replace = false): void {
		usePersistStore.setState((state) =>
			produce(state, (draft) => {
				if (replace) {
					draft.users[userID] = {
						...info,
						...this.validate_user_info(userID, info),
						timestamp: Date.now(),
						// fetch user timezone field not exists in the response so depend on the previous value
						...(draft.users[userID].timezone ? { timezone: draft.users[userID].timezone } : {})
					};
				} else {
					draft.users[userID] = {
						...draft.users[userID],
						...info,
						...this.validate_user_info(userID, info),
						timestamp: Date.now()
					};
				}
				// console.debug(LOGTAG, "set_user", draft.users[userID]);
			})
		);

		if (userID === Configure.userID) {
			Properties.property = { user: this.me };
		}
	}

	/**
	 * Updates the information of a user in the system.
	 *
	 * @param {PushServices.User.UserUpdated.Data} updated - The updated user data containing userInfo and updatedInfo.
	 * @return {Promise<void>} A promise that resolves when the user information has been updated.
	 */
	public async update_user(updated: PushServices.User.UserUpdated.Data): Promise<void> {
		const userID = updated.userInfo.id;

		let updated_user_info = updated.updatedInfo;
		const currentUserId = Configure.userID;
		/**
		 * Update 25/02/2025: When you first log in after signing up, your profile image behaves strangely.
		 * When the Unity capture the profile image in Fitting rooms. The actions update the user profile will execute.
		 * @ticket: 232922
		 * @author: DQTuan
		 */
		if (userID === currentUserId) {
			// Get user information stored in storage
			const currentUserInformation = this.me;

			if (!currentUserInformation) return;
			// When the user registered new account the profileImagePath will be undefined or empty. So we will update the profileImagePath captured from Unity
			if (!currentUserInformation?.profileImagePath || currentUserInformation?.profileImagePath?.length === 0) {
				// Get new user information
				const newUserInformation = await this.get_user_info(userID);
				if (!newUserInformation) return;

				// Set new profileImagePath to user
				this.set_user(currentUserId, {
					...currentUserInformation,
					profileImagePath: newUserInformation?.data?.profileImagePath
				});
			}
		}

		const { users } = usePersistStore.getState();
		if ((users && !users[userID]) || this.is_expired(userID)) {
			const user = await this.refresh_user(userID);
			updated_user_info = {
				...updated_user_info,
				...user
			};
		}
		const user = users[userID];

		const old_user_info = Object.entries(updated_user_info).reduce<{ [key: string]: any }>(
			(total, [key, value]) => {
				// @ts-ignore
				if (!has(user, key) || !user[key]) {
					return produce(total, (draft) => {
						draft[key] = value;
					});
				}
				// @ts-ignore
				const user_current = user[key];
				return produce(total, (draft) => {
					draft[key] = user_current;
				});
			},
			{}
		);

		if (!isEqual(updated_user_info, old_user_info)) {
			this.set_user(userID, updated_user_info);

			// console.groupCollapsed(
			// 	LOGTAG,
			// 	`User Updated: '${updated.updatedInfo.name}'(${userID}) ActiveStatus: ${updated.updatedInfo.activeStatus}`
			// );
			// console.debug(LOGTAG, "2", "updated_user_info", updated_user_info);
			// console.debug(LOGTAG, "3", "old_user_info", old_user_info);
			// console.groupEnd();
		}
	}

	/**
	 * Checks if a user session has expired based on the timestamp.
	 *
	 * @param {string} userID - The ID of the user to check.
	 * @return {boolean} - Returns `true` if the user session has expired, `false` otherwise.
	 */
	public is_expired(userID: string): boolean {
		const { users } = usePersistStore.getState();
		if (users && users[userID]) {
			const user = users[userID];
			if (user.timestamp ?? 0) {
				const timeout = Date.now() - (user.timestamp ?? Date.now());
				const result = timeout >= 60 * 1000 * 30; // 30min
				if (result) {
					Configure.useLogLevel === 10 &&
						console.debug(
							LOGTAG,
							`'${user.userId ?? user.id ?? user.userID}' has been expired.`,
							user.timestamp,
							timeout
						);
				}
				return result;
			}
		}
		return true;
	}

	/**
	 * Retrieves the active status of a user based on the provided user ID.
	 *
	 * @param {string} userID - The unique identifier of the user whose active status is to be fetched.
	 * @return {Promise<WebServices.User.UserInfo["activeStatus"]>} A promise that resolves to the active status of the user or undefined if the user is not found.
	 */
	public async active_status(userID: string): Promise<WebServices.User.UserInfo["activeStatus"]> {
		return this.get_user(userID)?.activeStatus ?? undefined;
	}

	/**
	 * Updates the active status of a user.
	 *
	 * @param {SwaggerModels.UserActiveStatus} activeStatus - The new active status to be updated.
	 * @return {Promise<void>} A promise that resolves when the active status is successfully updated.
	 */
	public async update_active_status(activeStatus: SwaggerModels.UserActiveStatus): Promise<void> {
		const userAPI = new UserService({});
		await userAPI.userActiveStatus.update.execute({ activeStatus });
		await this.update_me(null);
	}

	/**
	 * Checks if a user can be followed based on the provided user ID.
	 * @param userID
	 */
	public async check_before_follow(userID: string): Promise<UserCurrentSpace | undefined> {
		try {
			const userAPI = new UserService({});
			const data = await usePersistActions().users.refresh_user(userID, true);
			if (!data) {
				throw Error("DATA_NOT_FOUND");
			} else {
				if (data.activeStatus === "OFFLINE") {
					throw Error(_T("따라가기 할 수 없는 스페이스 입니다."));
				}
				const { currentSpace, currentConference } = data;

				const spaceType = checkPriorityCurrentSpaceType({ currentSpace, currentConference });
				if (spaceType === "SPACE") {
					const responseFlowData = await userAPI.user.checkBeforeFollowSpace.execute({
						userId: userID,
						spaceId: currentSpace?.spaceId,
						userSpaceType: currentSpace?.spaceType
					});
					/**
					 * Update 17/02/2025: Added popup confirmation register space when flow user with space is public
					 * @ticket: 235155
					 * @author: DQTuan
					 */
					if (responseFlowData?.status === 200 && currentSpace?.spaceType === "SPACE") {
						// Get spaceId of user before flow user with space
						const spaceId = currentSpace?.spaceId;
						const spaceAPI = new SpaceService({});

						// Get detail of space
						const detailSpace = await spaceAPI.space.detail.execute({ spaceId });

						// If space type is public space & user already joined group
						if (
							detailSpace?.data?.spaceAccessAllowType === "PUBLIC" &&
							detailSpace?.data?.groupJoinStatus === "JOINED"
						) {
							// Check if user already a space have joined
							const spaceJoinStatus = await spaceAPI.space.join.status.execute({ spaceId });
							if (spaceJoinStatus?.data) {
								const { notJoined } = spaceJoinStatus.data;
								if (notJoined) {
									// Show popup confirmation register space when user with space is public
									return Promise.reject({
										code: APIErrorCode.NOT_JOINED_SPACE,
										data: detailSpace?.data
									});
								}
							}
						}
					}

					return {
						spaceId: currentSpace?.spaceId || "",
						// @ts-ignore
						spaceType: currentSpace?.spaceType,
						// @ts-ignore
						spaceScene: currentSpace?.spaceScene || undefined
					};
				} else {
					// spaceType === "CONFERENCE"
					await userAPI.user.checkBeforeFollowConfernece.execute({
						userId: userID,
						conferenceId: currentConference?.conferenceId
					});

					// TODO: Write here for conference
					return {
						spaceId: currentConference?.conferenceId || "",
						// @ts-ignore
						spaceType: "CONFERENCE"
					};
				}
			}
		} catch (error) {
			console.error(LOGTAG, "check_before_follow", error);
			return Promise.reject(error);
		}
	}

	public async check_before_summon(userID: string): Promise<UserCurrentSpace | undefined> {
		try {
			const userAPI = new UserService({});
			const data = await usePersistActions().users.refresh_user(userID, true);

			if (!data) {
				throw Error("DATA_NOT_FOUND");
			} else {
				if (data.activeStatus === "OFFLINE") {
					throw Error(_T("따라가기 할 수 없는 스페이스 입니다."));
				}
				const { currentSpace, currentConference } = data;

				const spaceType = checkPriorityCurrentSpaceType({ currentSpace, currentConference });

				if (spaceType === "SPACE") {
					const responseFlowData = await userAPI.user.checkBeforeFollowSpace.execute({
						userId: userID,
						spaceId: currentSpace?.spaceId,
						userSpaceType: currentSpace?.spaceType
					});

					if (responseFlowData?.status === 200 && currentSpace?.spaceType === "SPACE") {
						// Get spaceId of user before flow user with space
						const spaceId = currentSpace?.spaceId;
						const spaceAPI = new SpaceService({});

						// Get detail of space
						const detailSpace = await spaceAPI.space.detail.execute({ spaceId });

						// If space type is public space & user already joined group
						if (
							detailSpace?.data?.spaceAccessAllowType === "PUBLIC" &&
							detailSpace?.data?.groupJoinStatus === "JOINED"
						) {
							// Check if user already a space have joined
							const spaceJoinStatus = await spaceAPI.space.join.status.execute({ spaceId });
							if (spaceJoinStatus?.data) {
								const { notJoined } = spaceJoinStatus.data;
								if (notJoined) {
									// Show popup confirmation register space when user with space is public
									return Promise.reject({
										code: APIErrorCode.NOT_JOINED_SPACE,
										data: detailSpace?.data
									});
								}
							}
						}
					}

					return {
						spaceId: currentSpace?.spaceId || "",
						// @ts-ignore
						spaceType: currentSpace?.spaceType,
						// @ts-ignore
						spaceScene: currentSpace?.spaceScene || undefined
					};
				} else {
					throw Error(_T("불러오기를 할 수 없는 공간입니다."));
				}
			}
		} catch (error) {
			console.error(LOGTAG, "check_before_summon", error);
			return Promise.reject(error);
		}
	}

	/**
	 * Retrieves detailed user information based on the provided user ID.
	 *
	 * @param {string} userID - The unique identifier of the user to inquire details about.
	 * @return {Promise<WebServices.User.InquiryUserDetail.Response | WebServices.User.Me.Inquiry.Response>} A promise that resolves to the user details response.
	 * @private
	 */
	private async get_user_info(
		userID: string
	): Promise<WebServices.User.InquiryUserDetail.Response | WebServices.User.Me.Inquiry.Response> {
		if (!Configure.is_authenticated || userID.length <= 0) {
			return { data: undefined };
		}
		const promises = [];
		promises.push(() => this.userAPI.user.inquiryUserDetail.execute({ userId: userID }));
		if (userID === Configure.userID) {
			promises.push(() => this.userAPI.user.me.inquiry.execute());
		}
		const results = await Promise.allSettled(promises.map((fn) => fn()));
		return results.reduce(
			(acc, o) => {
				if (o.status === "fulfilled") {
					// @ts-ignore
					acc.data = {
						...acc.data,
						...o.value.data
					};
				} else {
					acc.data = undefined;
				}
				return acc;
			},
			{
				data: {}
			} as WebServices.User.InquiryUserDetail.Response | WebServices.User.Me.Inquiry.Response
		);
	}

	/**
	 * Validates and updates the user information with the specified userID.
	 *
	 * @param {string} userID - The unique identifier for the user.
	 * @param {Partial<WebServices.User.UserInfo>} info - The partial user information to be validated and updated.
	 * @return {Partial<WebServices.User.UserInfo>} The validated and updated user information.
	 * @private
	 */
	private validate_user_info(
		userID: string,
		info: Partial<WebServices.User.UserInfo>
	): Partial<WebServices.User.UserInfo> {
		return produce(info, (draft) => {
			if (userID !== draft.id) draft.id = userID;
			if (userID !== draft.userId) draft.userId = userID;
			if (userID !== draft.userID) draft.userID = userID;
		});
	}
}
